"use client";

import * as React from "react";
import {
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "info" | "success" | "warning" | "error";
  title?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
  showIcon?: boolean;
  size?: "sm" | "md" | "lg";
  children?: React.ReactNode;
}

// Text and icons take the `-content` sibling; fills and borders keep the brand
// token. Using the fill as text put title at 2.23:1 and content at 1.87:1 in
// light mode, against a 4.5:1 AA floor.
//
// `content` drops its `/80`. Even on the corrected token, 80% opacity lands at
// 3.60:1 — the token swap alone does not rescue faded body text. Title/body
// hierarchy comes from `font-medium` on the title instead.
//
// `warning` is the exception: it already reads with `--warning-foreground`
// (Ink Plum) at 15.02:1 for the title and 8.64:1 for the content, so those are
// left exactly as they were. Only its icon used the fill token (2.17:1).
const variantConfig = {
  info: {
    container: "bg-info/10 border-info/30 text-info-content",
    icon: "text-info-content",
    title: "text-info-content",
    content: "text-info-content",
  },
  success: {
    container: "bg-success/10 border-success/30 text-success-content",
    icon: "text-success-content",
    title: "text-success-content",
    content: "text-success-content",
  },
  warning: {
    container: "bg-warning/10 border-warning/30 text-warning-foreground",
    icon: "text-warning-content",
    title: "text-warning-foreground",
    content: "text-warning-foreground/80",
  },
  error: {
    container: "bg-destructive/10 border-destructive/30 text-destructive-content",
    icon: "text-destructive-content",
    title: "text-destructive-content",
    content: "text-destructive-content",
  },
};

const defaultIcons = {
  info: <Info className="size-5" />,
  success: <CheckCircle2 className="size-5" />,
  warning: <AlertTriangle className="size-5" />,
  error: <XCircle className="size-5" />,
};

const sizeConfig = {
  sm: { container: "p-3", title: "text-sm font-medium", content: "text-sm" },
  md: { container: "p-4", title: "text-sm font-medium", content: "text-sm" },
  lg: { container: "p-6", title: "text-base font-medium", content: "text-sm" },
};

export const Alert: React.FC<AlertProps> = ({
  variant = "info",
  title,
  dismissible = false,
  onDismiss,
  icon,
  showIcon = true,
  size = "md",
  children,
  className,
  ...props
}) => {
  const config = variantConfig[variant];
  const sizeStyles = sizeConfig[size];
  const displayIcon = icon || (showIcon ? defaultIcons[variant] : null);

  return (
    <div
      className={cn(
        "rounded-xl border",
        config.container,
        sizeStyles.container,
        className
      )}
      role="alert"
      {...props}
    >
      <div className="flex">
        {displayIcon && (
          <div className="flex-shrink-0">
            <div className={config.icon}>{displayIcon}</div>
          </div>
        )}
        <div className={cn("flex-1", displayIcon && "ml-3")}>
          {title && (
            <h3
              className={cn(config.title, sizeStyles.title, children && "mb-1")}
            >
              {title}
            </h3>
          )}
          {children && (
            <div className={cn(config.content, sizeStyles.content)}>
              {children}
            </div>
          )}
        </div>
        {dismissible && onDismiss && (
          <div className="ml-auto pl-3">
            <button
              type="button"
              onClick={onDismiss}
              className={cn(
                "inline-flex rounded-md p-1.5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
                config.icon,
                "hover:bg-foreground/10"
              )}
              aria-label="Dismiss"
            >
              <X className="size-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

Alert.displayName = "Alert";

export interface ToastProps extends Omit<AlertProps, "dismissible" | "size"> {
  duration?: number;
  position?:
    | "top-right"
    | "top-left"
    | "bottom-right"
    | "bottom-left"
    | "top-center"
    | "bottom-center";
}

export const Toast: React.FC<ToastProps> = ({
  duration = 5000,
  position = "top-right",
  onDismiss,
  ...alertProps
}) => {
  React.useEffect(() => {
    if (duration > 0 && onDismiss) {
      const timer = setTimeout(onDismiss, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onDismiss]);

  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "top-center": "top-4 left-1/2 transform -translate-x-1/2",
    "bottom-center": "bottom-4 left-1/2 transform -translate-x-1/2",
  };

  return (
    <div
      className={cn("fixed z-50 max-w-sm w-full", positionClasses[position])}
    >
      <Alert
        {...alertProps}
        dismissible={true}
        onDismiss={onDismiss}
        size="md"
        className={cn("shadow-lg", alertProps.className)}
      />
    </div>
  );
};

Toast.displayName = "Toast";
